import { atom } from 'jotai';
import { atomWithStorage } from 'jotai/utils';

import type { VideoPlaybackState } from '../types';
import type { VideoFileMetadata } from '../utils/tracking';
import { extractFileMetadata } from '../utils/tracking';

/**
 * VIDEO PLAYER PRIMARY ATOMS
 * These atoms store the base state for the video player feature.
 * Following the same patterns as AudioPlayer atoms.
 */

/**
 * Store the video file as an ObjectURL
 * Set to null when no video is loaded
 * Must be revoked when clearing to prevent memory leaks
 */
export const videoFileAtom = atom<string | null>(null);
videoFileAtom.debugLabel = 'videoFileAtom';

/**
 * Store the reset point timestamp in seconds
 * When audio plays, video seeks to this position
 * Persists while video is loaded, resets to 0 on new video upload
 */
export const videoResetPointAtom = atom<number>(0);
videoResetPointAtom.debugLabel = 'videoResetPointAtom';

/**
 * Store the video element reference
 * Allows direct control of the native video APIs
 */
export const videoElementAtom = atom<HTMLVideoElement | null>(null);
videoElementAtom.debugLabel = 'videoElementAtom';

/**
 * Store video playback state
 * Tracks isPlaying, currentTime, and duration
 */
export const videoPlaybackAtom = atom<VideoPlaybackState>({
  isPlaying: false,
  currentTime: 0,
  duration: 0,
});
videoPlaybackAtom.debugLabel = 'videoPlaybackAtom';

/**
 * Track whether video player window is visible
 * Defaults to false - window opens when user uploads video
 */
export const videoPlayerVisibleAtom = atom<boolean>(false);
videoPlayerVisibleAtom.debugLabel = 'videoPlayerVisibleAtom';

/**
 * Store mute preference in localStorage - persists between sessions
 * Defaults to false (unmuted) so users can hear video audio
 */
export const videoMutedAtom = atomWithStorage<boolean>(
  'videoPlayerMuted',
  false // Default to unmuted
);
videoMutedAtom.debugLabel = 'videoMutedAtom';

/**
 * Store auto-sync preference in localStorage - persists between sessions
 * When enabled, video automatically resets and plays when audio plays
 * Defaults to true (enabled)
 */
export const videoAutoSyncAtom = atomWithStorage<boolean>(
  'videoPlayerAutoSync',
  true // Default to enabled
);
videoAutoSyncAtom.debugLabel = 'videoAutoSyncAtom';

/**
 * Store video upload/load error messages
 * Used to communicate errors from VideoPlayer back to VideoUploadPrompt
 * Cleared when user attempts a new upload
 */
export const videoUploadErrorAtom = atom<string | null>(null);
videoUploadErrorAtom.debugLabel = 'videoUploadErrorAtom';

/**
 * Store video loading state
 * True when a video is being loaded/validated
 * False once video successfully loads or fails
 * Keeps user on upload screen until video is confirmed working
 */
export const videoLoadingAtom = atom<boolean>(false);
videoLoadingAtom.debugLabel = 'videoLoadingAtom';

/**
 * Store video file metadata for tracking purposes
 * Contains size, format, name, extension
 * Used for ClickHouse analytics when video loads or errors
 */
export const videoFileMetadataAtom = atom<VideoFileMetadata | null>(null);
videoFileMetadataAtom.debugLabel = 'videoFileMetadataAtom';

/**
 * Store window size preference in localStorage - persists between sessions
 * Options: 'small' (320px), 'medium' (480px), 'large' (640px)
 * Defaults to 'medium'
 */
export const videoWindowSizeAtom = atomWithStorage<
  'small' | 'medium' | 'large'
>(
  'videoPlayerWindowSize',
  'medium' // Default to medium
);
videoWindowSizeAtom.debugLabel = 'videoWindowSizeAtom';

/**
 * ACTION ATOMS
 * These are write-only atoms that perform side effects.
 */

/**
 * Upload a video file and create ObjectURL
 * Automatically cleans up previous ObjectURL to prevent memory leaks
 * Shows player window after successful upload
 * Stores file metadata for tracking purposes
 */
export const uploadVideoFileAtom = atom(null, (get, set, file: File) => {
  // Clear any previous errors and set loading state
  set(videoUploadErrorAtom, null);
  set(videoLoadingAtom, true);

  // Extract and store file metadata for tracking
  const metadata = extractFileMetadata(file);
  set(videoFileMetadataAtom, metadata);

  // Revoke previous ObjectURL if exists
  const previousUrl = get(videoFileAtom);
  if (previousUrl) {
    URL.revokeObjectURL(previousUrl);
  }

  // Create new ObjectURL
  const objectUrl = URL.createObjectURL(file);
  set(videoFileAtom, objectUrl);
  set(videoPlayerVisibleAtom, true); // Show player after upload
  set(videoResetPointAtom, 0); // Reset to start for new video
});

/**
 * Set the reset point at current video time
 * Stores the current playback position as the new reset point
 */
export const setVideoResetPointAtom = atom(null, (get, set) => {
  const videoElement = get(videoElementAtom);
  if (videoElement) {
    const currentTime = videoElement.currentTime;
    set(videoResetPointAtom, currentTime);
  }
});

/**
 * Reset video to the configured reset point
 * Seeks the video element to the reset point timestamp
 */
export const resetVideoAtom = atom(null, (get, _set) => {
  const videoElement = get(videoElementAtom);
  const resetPoint = get(videoResetPointAtom);

  if (videoElement) {
    videoElement.currentTime = resetPoint;
  }
});

/**
 * Clear video and reset all state
 * Revokes ObjectURL and resets all atoms to initial state
 */
export const clearVideoAtom = atom(null, (get, set) => {
  const videoUrl = get(videoFileAtom);
  if (videoUrl) {
    URL.revokeObjectURL(videoUrl);
  }

  set(videoFileAtom, null);
  set(videoFileMetadataAtom, null);
  set(videoResetPointAtom, 0);
  set(videoPlaybackAtom, {
    isPlaying: false,
    currentTime: 0,
    duration: 0,
  });
  set(videoPlayerVisibleAtom, false);
});

/**
 * Clear video but keep player window open
 * Revokes ObjectURL and resets playback state, but keeps window visible
 * Used when user wants to change/upload a different video
 * Note: Keeps file metadata for error tracking purposes
 */
export const clearVideoKeepOpenAtom = atom(null, (get, set) => {
  const videoUrl = get(videoFileAtom);
  if (videoUrl) {
    URL.revokeObjectURL(videoUrl);
  }

  set(videoFileAtom, null);
  set(videoResetPointAtom, 0);
  set(videoLoadingAtom, false);
  set(videoPlaybackAtom, {
    isPlaying: false,
    currentTime: 0,
    duration: 0,
  });
  // Keep videoPlayerVisibleAtom as-is (don't close the window)
  // Keep videoFileMetadataAtom for error tracking purposes
});

/**
 * Toggle mute state
 * Updates both the atom and the video element
 */
export const toggleVideoMuteAtom = atom(null, (get, set) => {
  const currentMuted = get(videoMutedAtom);
  const videoElement = get(videoElementAtom);

  set(videoMutedAtom, !currentMuted);

  if (videoElement) {
    videoElement.muted = !currentMuted;
  }
});

/**
 * Toggle auto-sync state
 * Enables/disables automatic video reset when audio plays
 */
export const toggleVideoAutoSyncAtom = atom(null, (get, set) => {
  const currentAutoSync = get(videoAutoSyncAtom);
  set(videoAutoSyncAtom, !currentAutoSync);
});

/**
 * Whether the sync preview pulse animation is currently active.
 * Set to true after the 2nd track plays (with a 2s delay).
 * Set to false when dismissed by hover/click/timeout.
 */
export const syncPreviewPulseAtom = atom<boolean>(false);
syncPreviewPulseAtom.debugLabel = 'syncPreviewPulseAtom';

/**
 * Whether the pulse has been dismissed in the current session.
 * Once true, the pulse will not retrigger.
 * Intentionally a plain atom (not atomWithStorage) so it resets on page refresh,
 * matching the "same listening session" requirement.
 */
export const syncPreviewPulseDismissedAtom = atom<boolean>(false);
syncPreviewPulseDismissedAtom.debugLabel = 'syncPreviewPulseDismissedAtom';

/**
 * Dismiss the sync preview pulse.
 * Stops the current pulse and prevents retriggering.
 */
export const dismissSyncPreviewPulseAtom = atom(null, (_get, set) => {
  set(syncPreviewPulseAtom, false);
  set(syncPreviewPulseDismissedAtom, true);
});
